import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { BarChart2, X } from "lucide-react";

const labelSx = {
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "hsl(150 10% 46%)",
  mb: 0.5,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
};

const nativeInputSx = {
  width: "100%",
  height: 40,
  borderRadius: "6px",
  border: "1px solid hsl(140 8% 88%)",
  backgroundColor: "hsl(140 10% 95%)",
  px: 1.5,
  py: 1,
  fontSize: 14,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "hsl(150 30% 12%)",
  outline: "none",
  boxSizing: "border-box",
  "&::placeholder": { color: "hsl(150 10% 46%)" },
  "&:focus": { outline: "2px solid hsl(150 38% 17%)", outlineOffset: "2px" },
};

const NewStatsPageModal = ({ open, onClose, onSubmit, submitting, statsPage }) => {
  const isEditing = Boolean(statsPage);
  const [title, setTitle] = useState("");

  useEffect(() => {
    if (!open) {
      setTitle("");
    } else if (statsPage) {
      setTitle(statsPage.title || "");
    }
  }, [open, statsPage]);

  const canSubmit = useMemo(() => title.trim().length > 0, [title]);

  const handleClose = () => onClose?.();

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit?.({ title: title.trim() }, {
      reset: () => setTitle(""),
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && canSubmit && !submitting) handleSubmit();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          px: 2.5,
          py: 2.5,
          backgroundColor: "hsl(150 38% 17%)",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              backgroundColor: "hsla(80, 97%, 72%, 0.15)",
              borderRadius: "10px",
              p: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BarChart2 size={20} color="#fff" />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 600,
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                color: "#fff",
              }}
            >
              {isEditing ? "Edit stats page" : "New stats page"}
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                color: "hsla(80, 97%, 72%, 0.7)",
              }}
            >
              {isEditing ? "Rename this stats page" : "Create a new dashboard page"}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          disableRipple
          size="small"
          onClick={handleClose}
          sx={{
            p: 0.5,
            borderRadius: "6px",
            color: "hsla(80, 97%, 72%, 0.7)",
            "&:hover": { backgroundColor: "hsla(255,255,255,0.1)" },
          }}
        >
          <X size={18} />
        </IconButton>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 2.5, backgroundColor: "#fff" }}>
        <Stack spacing={2}>
          <Box>
            <Typography sx={labelSx}>Page title *</Typography>
            <Box
              component="input"
              type="text"
              placeholder="e.g. Candidate flanders"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              sx={nativeInputSx}
            />
          </Box>
        </Stack>
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          borderTop: "1px solid hsl(140 8% 88%)",
          backgroundColor: "#fff",
        }}
      >
        <Button
          disableRipple
          onClick={handleClose}
          disabled={submitting}
          sx={{
            textTransform: "none",
            fontSize: 14,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            color: "hsl(150 10% 46%)",
            borderRadius: "8px",
            px: 2,
            "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
          }}
        >
          Cancel
        </Button>
        <Button
          disableRipple
          disableElevation
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          sx={{
            textTransform: "none",
            fontSize: 14,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            borderRadius: "8px",
            px: 3,
            py: 1,
            minHeight: 0,
            height: "auto",
            backgroundColor: "hsl(150 38% 17%)",
            color: "hsl(80 97% 72%)",
            transition: "opacity 0.15s ease",
            "&:hover": {
              backgroundColor: "hsl(150 38% 17%)",
              opacity: 0.9,
            },
            "&.Mui-disabled": {
              opacity: 0.5,
              backgroundColor: "hsl(150 38% 17%)",
              color: "hsl(80 97% 72%)",
              pointerEvents: "none",
            },
          }}
        >
          {submitting ? (
            <CircularProgress size={16} sx={{ color: "hsl(80 97% 72%)", mr: 1 }} />
          ) : null}
          {submitting
            ? isEditing ? "Saving..." : "Creating..."
            : isEditing ? "Save changes" : "Create page"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewStatsPageModal;
